/**
 * Der Lesemodus für E-Mails — je Konto, je Gerät. (20.10.2026, Martin: „Albert kann oft nicht hören und möchte E-Mails dann lesen“)
 *
 * Ein Schalter, ein Besitzer: dieses Modul. Der Zustand steht in localStorage unter einem Schlüssel MIT Kontokennung
 * (`voice-v8.lesemodus.<uid>`) — ein Kontowechsel auf demselben Gerät mischt nichts (kein Schlüssel eines anderen Kontos wird je gelesen).
 * Standard AUS. Ohne bekannte Kennung oder ohne Speicher gilt der Wert nur bis zum Neuladen (nie ein Schlüssel ohne Konto).
 *
 * Wirkung: die Klasse `lesemodus` auf <html> (alle Maße und Farben in frontend/style.css, Block „LESEMODUS“),
 * kein automatisches Vorlesen von Mail-Antworten (`mailStimmeAus`), Schalter-Knöpfe in Karte und Einstellungen ziehen nach.
 */
import { kontoUid } from "./konto-lage.js";

export const LESEMODUS_PRAEFIX = "voice-v8.lesemodus.";
export const LESEMODUS_KLASSE = "lesemodus";
export const LESEMODUS_EREIGNIS = "voice-v8:lesemodus";
/** Knöpfe, die den Zustand zeigen (Mail-Karte) — sie tragen aria-pressed und das Wort. */
export const LESEMODUS_KNOPF_KLASSE = "tool-mail-lesemodus";

let ohneKonto = false;
/** Zähler der Züge (Antworten auf eine Frage) und die Züge, in denen LIVE eine Mail-Karte erschien — je Zug, nie global. */
let zugNr = 0;
const mailZuege = new Set<number>();

function schluessel(): string | null {
  const uid = kontoUid();
  return uid === null ? null : `${LESEMODUS_PRAEFIX}${uid}`;
}

/** Ist der Lesemodus für das angemeldete Konto an? Nur ein ausdrückliches „1“ zählt. */
export function lesemodusAn(): boolean {
  const k = schluessel();
  if (k === null) return ohneKonto;
  try {
    return localStorage.getItem(k) === "1";
  } catch {
    return ohneKonto;
  }
}

function zeichneKnopf(knopf: HTMLButtonElement, an: boolean): void {
  // Fester Name = sichtbares Wort (WCAG 2.5.3); der Zustand steht NUR in aria-pressed und im Stil, nicht im Text.
  knopf.setAttribute("aria-pressed", String(an));
}

/** Klasse auf <html> und alle Knöpfe dem Zustand angleichen. */
export function zeichneLesemodus(): void {
  const an = lesemodusAn();
  document.documentElement.classList.toggle(LESEMODUS_KLASSE, an);
  for (const k of document.querySelectorAll<HTMLButtonElement>(`.${LESEMODUS_KNOPF_KLASSE}`)) zeichneKnopf(k, an);
}

/** Beim Start und nach dem Anmelden aufrufen: das Konto kennt jetzt seinen Zustand. */
export function uebernimmLesemodus(): void {
  zeichneLesemodus();
}

export function setzeLesemodus(an: boolean): void {
  const k = schluessel();
  ohneKonto = k === null ? an : false;
  if (k !== null) {
    try {
      localStorage.setItem(k, an ? "1" : "0");
    } catch {
      ohneKonto = an; // kein Speicher: gilt bis zum Neuladen
    }
  }
  zeichneLesemodus();
  window.dispatchEvent(new CustomEvent(LESEMODUS_EREIGNIS, { detail: { an } }));
}

/** Der Schalter an der Mail-Karte: ein Knopf, ≥ 44 px (CSS), aria-pressed. */
export function lesemodusKnopf(): HTMLButtonElement {
  const knopf = document.createElement("button");
  knopf.type = "button";
  knopf.className = LESEMODUS_KNOPF_KLASSE;
  knopf.textContent = "Lesemodus";
  knopf.title = "Lesemodus für E-Mails: große Schrift, kein automatisches Vorlesen";
  zeichneKnopf(knopf, lesemodusAn());
  knopf.addEventListener("click", () => setzeLesemodus(!lesemodusAn()));
  return knopf;
}

/** Ein neuer Zug beginnt (Sprach- oder Textantwort). Ein laufender älterer Zug bleibt unberührt. */
export function beginneMailZug(): number {
  // Chat-Züge räumen sich nicht selbst ab (ihr Vorlesen am Zugende kommt nach dem finally): alte Nummern fallen hier heraus.
  for (const n of mailZuege) if (n < zugNr - 20) mailZuege.delete(n);
  return ++zugNr;
}

export function beendeMailZug(nr: number): void {
  mailZuege.delete(nr);
}

const MAIL_WERKZEUGE = new Set(["gmail_search", "gmail_read_message"]);

/**
 * Das Ergebnis eines Werkzeugs kam im Zug `nr` an: war es eine Mail-Karte und der Zug nicht abgelöst, ist DIESER Zug still
 * (Lesemodus) — `stumm` bricht ab, was schon spricht. Nie beim Neuaufbau aus Verlauf oder Archiv (dort läuft kein Zug).
 */
export function merkeMailAntwort(nr: number, werkzeug: string, abgeloest: boolean, stumm: () => void): void {
  if (abgeloest || !MAIL_WERKZEUGE.has(werkzeug)) return;
  mailZuege.add(nr);
  if (lesemodusAn()) stumm();
}

/** Soll die Stimme die Antwort dieses Zuges NICHT von selbst sprechen? Das Vorlesen per Knopf bleibt davon unberührt. */
export function mailStimmeAus(nr?: number): boolean {
  return nr !== undefined && mailZuege.has(nr) && lesemodusAn();
}
